// React hooks over the control plane: the signed-in user, live documents (onSnapshot), a small
// stale-while-revalidate cache for JSON routes, and pollers that pause while the tab is hidden.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { AuthUser } from "@estra/client/auth";
import { collection, doc, onSnapshot, orderBy, query, where, type DocumentData } from "@estra/client/firestore";
import { auth, db } from "../api.ts";

export function useUser(): AuthUser | null | undefined {
  const [u, setU] = useState<AuthUser | null | undefined>(undefined);
  useEffect(() => auth.onAuthStateChanged((x) => setU(x ?? null)), []);
  return u;
}

export interface Live<T> {
  data: T | undefined;
  error: Error | null;
}

/** One document, live. `undefined` while loading, `null` when it doesn't exist. */
export function useDoc(path: string | null): Live<(DocumentData & { id: string }) | null> {
  const [s, set] = useState<Live<(DocumentData & { id: string }) | null>>({ data: undefined, error: null });
  useEffect(() => {
    if (!path) return;
    set({ data: undefined, error: null });
    return onSnapshot(
      doc(db, path),
      (snap) => set({ data: snap.exists() ? { ...snap.data()!, id: snap.id } : null, error: null }),
      (e) => set({ data: undefined, error: e as Error }),
    );
  }, [path]);
  return s;
}

export type Project = DocumentData & { id: string };

/** The signed-in user's projects, newest first, live. */
export function useProjects(uid: string | undefined): Live<Project[]> {
  const [s, set] = useState<Live<Project[]>>({ data: undefined, error: null });
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(
      query(collection(db, "projects"), where("ownerUid", "==", uid), orderBy("createdAt", "desc")),
      (snap) => set({ data: snap.docs.map((d) => ({ ...d.data(), id: d.id })), error: null }),
      (e) => set({ data: undefined, error: e as Error }),
    );
  }, [uid]);
  return s;
}

// --- a tiny SWR cache: one entry per key, shared by every component that asks for it ---
interface Entry {
  data?: unknown;
  error?: Error | null;
  at: number;
  inflight?: Promise<void>;
  subs: Set<() => void>;
}
const cache = new Map<string, Entry>();
const entry = (k: string) => {
  let e = cache.get(k);
  if (!e) cache.set(k, (e = { at: 0, subs: new Set() }));
  return e;
};
function revalidate(k: string, fn: () => Promise<unknown>) {
  const e = entry(k);
  if (e.inflight) return e.inflight;
  e.inflight = fn()
    .then((d) => void Object.assign(e, { data: d, error: null, at: Date.now() }))
    .catch((err) => void Object.assign(e, { error: err as Error, at: Date.now() }))
    .finally(() => {
      e.inflight = undefined;
      e.subs.forEach((s) => s());
    });
  return e.inflight;
}
/** Drops cached answers whose key starts with `prefix` (after a write), refetching the ones in view. */
export function invalidate(prefix: string) {
  for (const [k, e] of cache) if (k.startsWith(prefix)) Object.assign(e, { at: 0 }), e.subs.forEach((s) => s());
}

export interface Loaded<T> {
  data: T | undefined;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

/** Cached `fn()` under `key` (null: skip). Fresh for `maxAge` ms; stale data shows while it refetches. */
export function useLoad<T>(key: string | null, fn: () => Promise<T>, maxAge = 30_000): Loaded<T> {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const subscribe = useCallback((cb: () => void) => {
    if (!key) return () => {};
    const e = entry(key);
    e.subs.add(cb);
    return () => void e.subs.delete(cb);
  }, [key]);
  const snap = useSyncExternalStore(subscribe, () => (key ? cache.get(key) : undefined)?.at ?? -1);
  const e = key ? cache.get(key) : undefined;
  useEffect(() => {
    if (!key) return;
    const cur = entry(key);
    if (Date.now() - cur.at > maxAge) void revalidate(key, () => fnRef.current());
  }, [key, snap, maxAge]);
  const reload = useCallback(() => {
    if (key) void revalidate(key, () => fnRef.current());
  }, [key]);
  return { data: e?.data as T | undefined, error: e?.error ?? null, loading: !!key && (!e || e.at === 0 || !!e.inflight), reload };
}

/** True while the page is visible. */
export function useVisible(): boolean {
  return useSyncExternalStore(
    (cb) => {
      document.addEventListener("visibilitychange", cb);
      return () => document.removeEventListener("visibilitychange", cb);
    },
    () => document.visibilityState === "visible",
  );
}

/** Calls `fn` now and every `ms` while the tab is visible (and `on`). */
export function usePoll(fn: () => void, ms: number, on = true) {
  const visible = useVisible();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!on || !visible) return;
    ref.current();
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [ms, on, visible]);
}

/** A value in localStorage (per-viewer conveniences only), surviving a blocked or empty store. */
export function useStored<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const s = localStorage.getItem(key);
      return s === null ? initial : (JSON.parse(s) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback((x: T) => {
    setV(x);
    try {
      localStorage.setItem(key, JSON.stringify(x));
    } catch {
      /* private window: keep it in memory */
    }
  }, [key]);
  return [v, set];
}

/** Ticks every `ms`, for "updated 12 s ago" labels. */
export function useNow(ms = 1000): number {
  const [n, setN] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setN(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return n;
}
